import config from '../../../config'
import http from './http-util'

class RepresentativeServiceApi {
  // REPRESENTATIVES

  static getRepresentatives = token => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/v2/limited`,
    )
  }

  static searchRepresentatives =
    ({ pageInfo, searchInfo }) =>
    token => {
      const queryParams = pageInfo.buildParam()
      const requestBody = {
        withoutCoreInfo: true,
        representativeSearchCriteria: {
          searchString: searchInfo.search || '',
          fields: [
            'POPULAR_NAME',
            'TRADE_NAME',
            'ACCOUNT_SYSTEM_CHAIN_ID',
            'ORG_NUMBER',
          ],
        },
      }
      return http.post(
        token,
        `${config.representativeServiceUrl}/representatives/v2/search?${queryParams}`,
        JSON.stringify(requestBody),
      )
    }

  static getRepresentative = (id, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${id}`,
    )
  }

  static putRepresentative = (representativeId, requestData) => token => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${representativeId}`,
      JSON.stringify(requestData),
    )
  }

  static getRepresentativePaymentSettings = (representativeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/payment_settings`,
    )
  }

  static getStorePaymentSettings = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/payment_settings`,
    )
  }

  // STORES

  static getRepresentativeStores = (id, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${id}/stores`,
    )
  }

  static searchRepresentativeStores =
    ({ representativeId, pageInfo, searchInfo }) =>
    token => {
      const queryParams = pageInfo.buildParam()
      const requestBody = {
        withoutCoreInfo: true,
        representativeStoreSearchCriteria: {
          searchString: searchInfo.search || '',
          fields: ['TRADE_NAME', 'STORE_ID', 'ORG_NUMBER', 'POPULAR_NAME'],
        },
      }
      return http.post(
        token,
        `${config.representativeServiceUrl}/representatives/v2/${representativeId}/stores/search?${queryParams}`,
        JSON.stringify(requestBody),
      )
    }

  static getLimitedRepresentativeStores = repId => token => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${repId}/stores/limited`,
    )
  }

  static getLimitedStores = storeIds => token => {
    return http.post(
      token,
      `${config.representativeServiceUrl}/stores/v2/info`,
      JSON.stringify({ stores: storeIds }),
    )
  }

  static getStores = (ids, token) => {
    const idsParams = ids.join(',')
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/v2?ids=${idsParams}`,
    )
  }

  static getStore = (id, token) => {
    return http.get(token, `${config.representativeServiceUrl}/stores/v2/${id}`)
  }

  static putStore = (storeId, requestData) => token => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/stores/v2/${storeId}`,
      JSON.stringify(requestData),
    )
  }

  static getStoreDebitEnabled = storeId => token =>
    http.get(
      token,
      `${config.representativeServiceUrl}/stores/v2/${storeId}/debit_enabled`,
    )

  // INVOICE NUMBER

  static getRepresentativeInvoiceNumber = (representativeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/invoice_numbers`,
    )
  }

  static getStoreInvoiceNumber = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/invoice_numbers`,
    )
  }

  static getNextInvoiceNumber = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/invoice_numbers/storeId/${storeId}`,
    )
  }

  static putRepresentativeInvoiceNumber = (
    { representativeId, nextNumber },
    token,
  ) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/invoice_numbers/${nextNumber}`,
    )
  }

  static putStoreInvoiceNumber = ({ storeId, nextNumber }, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/invoice_numbers/${nextNumber}`,
    )
  }

  // TERMS LINKS

  static updateRepresentativeTermsLink = (requestData, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${requestData.representativeId}/terms_links/${requestData.language}`,
      JSON.stringify({ link: requestData.termsLink }),
    )
  }

  static updateStoreTermsLink = (requestData, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/stores/v2/${requestData.storeId}/terms_links/${requestData.language}`,
      JSON.stringify({ link: requestData.termsLink }),
    )
  }

  // CART ITEM MERGE PARAMS

  static getRepresentativeCartItemMergeParams = (representativeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/payment_settings/cart_item_merge_params`,
    )
  }

  static getStoreCartItemMergeParams = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/payment_settings/cart_item_merge_params`,
    )
  }

  static putRepresentativeCartItemMergeParams = (
    { id, requestBody },
    token,
  ) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/representatives/${id}/payment_settings/cart_item_merge_params`,
      JSON.stringify(requestBody),
    )
  }

  static putStoreCartItemMergeParams = ({ id, requestBody }, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/stores/${id}/payment_settings/cart_item_merge_params`,
      JSON.stringify(requestBody),
    )
  }

  // PAYMENT METHODS

  static getRepresentativePaymentMethods =
    (representativeId, channelType) => token => {
      return http.get(
        token,
        `${config.representativeServiceUrl}/representatives/${representativeId}/payment_methods/channel_type/${channelType}`,
      )
    }

  static getStorePaymentMethods = (storeId, channelType) => token => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/v2/${storeId}/channel_type/${channelType}/payment_methods`,
    )
  }

  static updateOrderDateAndFee = (channelType, requestData) => token => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/payment_methods/channel/${channelType}/updateOrderDateAndFee`,
      JSON.stringify(requestData),
    )
  }

  // INVOICE DATA

  static getStoreInvoiceData = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/invoice_data`,
    )
  }

  static putRepresentativeInvoiceData = (requestData, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/representatives/${requestData.representativeId}/invoice_data`,
      JSON.stringify(requestData.newValues),
    )
  }

  static putStoreInvoiceData = (requestData, token) => {
    return http.put(
      token,
      `${config.representativeServiceUrl}/stores/${requestData.storeId}/invoice_data`,
      JSON.stringify(requestData.newValues),
    )
  }

  // LOGOTYPE

  static getStoreLogotype = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/logotype`,
    )
  }

  static postRepresentativeLogotype = (requestData, token) => {
    return http.post(
      token,
      `${config.representativeServiceUrl}/representatives/${requestData.representativeId}/logotype
      `,
      JSON.stringify({
        logotype: requestData.logotype,
        fileName: requestData.fileName,
      }),
    )
  }

  static postStoreLogotype = (requestData, token) => {
    return http.post(
      token,
      `${config.representativeServiceUrl}/stores/${requestData.storeId}/logotype
      `,
      JSON.stringify({
        logotype: requestData.logotype,
        fileName: requestData.fileName,
      }),
    )
  }

  // CHECKOUT SETTINGS

  static getRepresentativeCheckoutSettings = (representativeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/checkout_settings`,
    )
  }

  static getStoreCheckoutSettings = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/checkout_settings`,
    )
  }

  static getRepresentativeTermsLinks = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/representatives/v2/${storeId}/terms_links`,
    )
  }

  static getStoreTermsLinks = (storeId, token) => {
    return http.get(
      token,
      `${config.representativeServiceUrl}/stores/v2/${storeId}/terms_links`,
    )
  }

  static postRepresentativeCheckoutSettings = (
    { representativeId, checkoutStyling },
    token,
  ) => {
    return http.post(
      token,
      `${config.representativeServiceUrl}/representatives/${representativeId}/checkout_settings`,
      JSON.stringify({ checkoutStyling }),
    )
  }

  static postStoreCheckoutSettings = ({ storeId, checkoutStyling }, token) => {
    return http.post(
      token,
      `${config.representativeServiceUrl}/stores/${storeId}/checkout_settings`,
      JSON.stringify({ checkoutStyling }),
    )
  }
}

export default RepresentativeServiceApi
